import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { unwrapPaginated } from "@/config/api";
import { notAssessedLabel } from "@/lib/trustLabels";
import type { Moderator, ModeratorDeal } from "@/services/moderators/generated/model";
import {
  getModeratorsDealsListQueryKey,
  useModeratorsDealsAssignCreate,
  useModeratorsDealsCommentsCreate,
  useModeratorsDealsList,
} from "@/services/moderators/generated/moderators-deals/moderators-deals";
import { useModeratorsList } from "@/services/moderators/generated/moderators-moderators/moderators-moderators";
import "./ModeratorPage.css";
import "./ModeratorDealsPage.css";
import { outcomeLabel, outcomeVariant } from "@/lib/dealOutcome";
import { consentLabel, consentVariant } from "@/lib/supplierConsent";
import ManualDealModal from "./ManualDealModal";

const ModeratorDealsPage = () => (
  <ModeratorLayout>{(moderator) => <DealsContent isSuper={moderator.role === "SUPERMODERATOR"} />}</ModeratorLayout>
);

const DealsContent = ({ isSuper }: { isSuper: boolean }) => {
  const { t } = useTranslation(["deals", "common"]);
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [openDeal, setOpenDeal] = useState<number | null>(null);
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);

  const queryParams = useMemo(() => (search.trim() ? { search: search.trim() } : undefined), [search]);
  const { data, isLoading, error } = useModeratorsDealsList(queryParams, {
    query: { enabled: isSuper },
  });
  const deals = unwrapPaginated<ModeratorDeal>(data);

  const { data: moderatorsData } = useModeratorsList(undefined, { query: { enabled: isSuper } });
  const moderators = unwrapPaginated<Moderator>(moderatorsData).filter((m) => m.isActive);

  const assignMutation = useModeratorsDealsAssignCreate({
    mutation: {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: getModeratorsDealsListQueryKey(queryParams) }),
    },
  });
  const commentMutation = useModeratorsDealsCommentsCreate({
    mutation: {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: getModeratorsDealsListQueryKey(queryParams) }),
    },
  });

  if (!isSuper) {
    return (
      <>
        <header className="mod-page__header">
          <div>
            <h1 className="mod-page__title">{t("deals.title")}</h1>
            <p className="mod-page__subtitle">{t("deals.restrictedSubtitle")}</p>
          </div>
        </header>
        <div className="mod-page__empty">{t("deals.restrictedBody")}</div>
      </>
    );
  }

  return (
    <div>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("deals.title")}</h1>
          <p className="mod-page__subtitle">{t("deals.subtitle")}</p>
        </div>
        <button
          type="button"
          className="mod-page__btn mod-page__btn--ghost"
          onClick={() => queryClient.invalidateQueries({ queryKey: getModeratorsDealsListQueryKey(queryParams) })}
          disabled={isLoading}
          aria-label={t("deals.refreshAria")}
        >
          <i className={`fa-solid ${isLoading ? "fa-spinner fa-spin" : "fa-arrows-rotate"}`} />
          <span style={{ marginLeft: 6 }}>{t("common:actions.refresh")}</span>
        </button>
        <button
          type="button"
          className="mod-page__btn mod-page__btn--primary"
          onClick={() => setIsManualModalOpen(true)}
        >
          <i className="fa-solid fa-plus" />
          <span style={{ marginLeft: 6 }}>{t("manualDeal.title")}</span>
        </button>
      </header>

      {isManualModalOpen ? (
        <ManualDealModal
          onClose={() => setIsManualModalOpen(false)}
          onCreated={() => queryClient.invalidateQueries({ queryKey: getModeratorsDealsListQueryKey(queryParams) })}
        />
      ) : null}

      <form
        className="mod-page__toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(searchInput);
        }}
      >
        <input
          type="search"
          className="mod-page__input"
          placeholder={t("deals.searchPlaceholder")}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <button type="submit" className="mod-page__btn mod-page__btn--primary">
          {t("common:actions.apply")}
        </button>
        <button
          type="button"
          className="mod-page__btn mod-page__btn--ghost"
          onClick={() => {
            setSearchInput("");
            setSearch("");
          }}
        >
          {t("shared.reset")}
        </button>
      </form>

      {isLoading ? <div className="mod-page__loading">{t("deals.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("deals.loadError")}</div> : null}
      {!isLoading && deals.length === 0 ? <div className="mod-page__empty">{t("deals.empty")}</div> : null}

      {deals.map((deal) => (
        <DealRow
          key={deal.id}
          deal={deal}
          moderators={moderators}
          isOpen={openDeal === deal.id}
          onToggle={() => setOpenDeal(openDeal === deal.id ? null : deal.id)}
          onAssign={(moderatorId) => assignMutation.mutate({ id: deal.id, data: { moderatorId } })}
          onComment={(text) => commentMutation.mutate({ id: deal.id, data: { text } })}
          isAssigning={assignMutation.isPending && assignMutation.variables?.id === deal.id}
          isCommenting={commentMutation.isPending && commentMutation.variables?.id === deal.id}
          assignError={
            assignMutation.isError && assignMutation.variables?.id === deal.id
              ? extractError(assignMutation.error, t("deals.assignFailed"))
              : null
          }
          commentError={
            commentMutation.isError && commentMutation.variables?.id === deal.id
              ? extractError(commentMutation.error, t("deals.commentFailed"))
              : null
          }
        />
      ))}
    </div>
  );
};

interface DealRowProps {
  deal: ModeratorDeal;
  moderators: Moderator[];
  isOpen: boolean;
  onToggle: () => void;
  onAssign: (moderatorId: number) => void;
  onComment: (text: string) => void;
  isAssigning: boolean;
  isCommenting: boolean;
  assignError: string | null;
  commentError: string | null;
}

const DealRow = ({
  deal,
  moderators,
  isOpen,
  onToggle,
  onAssign,
  onComment,
  isAssigning,
  isCommenting,
  assignError,
  commentError,
}: DealRowProps) => {
  const { t } = useTranslation(["deals", "common"]);
  const [assignTo, setAssignTo] = useState<string>("");
  const [comment, setComment] = useState("");

  return (
    <article className="mod-page__card mod-deals__card">
      <div className="mod-page__card-body">
        <h2 className="mod-page__card-title">{t("shared.dealTitle", { id: deal.id })}</h2>
        <p className="mod-page__card-meta">
          <Trans
            t={t}
            i18nKey="deals.row.parties"
            values={{
              requester: deal.requesterCompany?.companyName ?? "—",
              matched: deal.matchedCompany?.companyName ?? "—",
            }}
            components={{ strong: <strong /> }}
          />
        </p>
        {deal.matchRequest?.requirementTitle ? (
          <p className="mod-page__card-meta">
            {t("shared.requestLine", { title: deal.matchRequest.requirementTitle })}
          </p>
        ) : null}
        <p className="mod-page__card-meta">
          <Trans
            t={t}
            i18nKey="deals.row.scores"
            values={{
              score: deal.winWinMatchScore,
              industry: deal.industryMatch,
              compliance: deal.complianceCert ?? notAssessedLabel(),
              geo: deal.geographicLocation,
              exportExperience: deal.exportExperience ?? notAssessedLabel(),
            }}
            components={{ strong: <strong /> }}
          />
        </p>
        <p className="mod-page__card-meta">
          {t("deals.row.assigned", { name: deal.assignedModerator?.fullName ?? t("shared.unassigned") })}
        </p>
        <p className="mod-page__card-meta">
          <span className={`mod-page__chip mod-page__chip--${consentVariant(deal.consentStatus)}`}>
            {consentLabel(deal.consentStatus, "moderator")}
          </span>{" "}
          <span className={`mod-page__chip mod-page__chip--${outcomeVariant(deal.outcome.status)}`}>
            {t("deals.row.outcome", { label: outcomeLabel(deal.outcome.status) })}
          </span>
          {deal.isManual ? (
            <>
              {" "}
              <span className="mod-page__chip mod-page__chip--warning" title={deal.manualReason}>
                {t("shared.manualDeal")}
              </span>
            </>
          ) : null}
        </p>
        {deal.contract ? (
          <p className="mod-page__card-meta">
            <span className="mod-page__chip mod-page__chip--success">
              {t("deals.row.contract", { reference: deal.contract.referenceId || `#${deal.contract.id}` })}
            </span>
          </p>
        ) : null}
        {deal.comments && deal.comments.length > 0 ? (
          <p className="mod-page__card-meta">{t("deals.row.comments", { count: deal.comments.length })}</p>
        ) : null}

        {isOpen ? (
          <div className="mod-deals__detail">
            <div className="mod-deals__detail-block">
              <h3>{t("deals.row.assignHeading")}</h3>
              <div className="mod-page__form-row">
                <select className="mod-page__select" value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
                  <option value="">{t("shared.selectModerator")}</option>
                  {moderators.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.fullName} ({m.email})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="mod-page__btn mod-page__btn--primary"
                  disabled={!assignTo || isAssigning}
                  onClick={() => onAssign(Number(assignTo))}
                >
                  {isAssigning ? t("shared.assigning") : t("shared.assign")}
                </button>
              </div>
              {assignError ? <p className="mod-page__inline-error">{assignError}</p> : null}
            </div>

            <div className="mod-deals__detail-block">
              <h3>{t("deals.row.commentHeading")}</h3>
              <textarea
                className="mod-page__textarea"
                placeholder={t("deals.row.commentPlaceholder")}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <div className="mod-page__form-row">
                <button
                  type="button"
                  className="mod-page__btn mod-page__btn--primary"
                  disabled={!comment.trim() || isCommenting}
                  onClick={() => {
                    onComment(comment.trim());
                    setComment("");
                  }}
                >
                  {isCommenting ? t("shared.posting") : t("shared.postComment")}
                </button>
              </div>
              {commentError ? <p className="mod-page__inline-error">{commentError}</p> : null}
            </div>
          </div>
        ) : null}
      </div>

      <div className="mod-deals__actions">
        <Link to={`/moderator/deals/${deal.id}`} className="mod-page__btn mod-page__btn--primary">
          {t("deals.row.open")}
        </Link>
        <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={onToggle}>
          {isOpen ? t("common:actions.close") : t("deals.row.manage")}
        </button>
      </div>
    </article>
  );
};

function extractError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string } }; data?: { error?: string } } | null | undefined;
  return e?.response?.data?.error ?? e?.data?.error ?? fallback;
}

export default ModeratorDealsPage;
